import { getAuth } from "firebase/auth";

import { strings } from "../@strings/strings";

type OmitFunctions<T> = {
	[K in keyof T as T[K] extends (...args: any) => any ? never : K]: T[K];
};

export type SoloClass<T extends new (...args: any) => any, B = HTMLElement> = OmitFunctions<Omit<InstanceType<T>, keyof B>>;

export function withTimeout<T>(promise: Promise<T>, timeout: number) {
	return Promise.race([promise, new Promise<T>((_, reject) => setTimeout(() => reject(new Error("Timeout exceeded")), timeout))]);
}
let errors = {
	1: {
		status: 404 as const,
		error_type: "UserNotFoundException" as const,
		error_msg: "User id/username not found in database"
	},
	2: {
		status: 400 as const,
		error_type: "BadLicenseException" as const,
		error_msg: "BadLicenseException"
	},
	3: {
		status: 400 as const,
		error_type: "BadUsernameException" as const,
		error_msg: 'No puedes asignarle a una persona un "administrador"'
	},
	4: {
		status: 400 as const,
		error_type: "InvalidRequestException" as const,
		error_msg: "No has const"
	},
	5: {
		status: 401 as const,
		error_type: "UnauthorizedException" as const,
		error_msg: "El usuario no ha aplicado a la plaza de trabajo o ha abandonado el proceso"
	},
	6: {
		status: 400 as const,
		error_type: "EmailAlreadyExistsException" as const,
		error_msg: "Email already exists"
	},
	7: {
		status: 507 as const,
		error_type: "InsufficientStorageException" as const,
		error_msg: "You have uploaded more than 100 MB, you need to update your plan to upload more images"
	},
	8: {
		status: 400 as const,
		error_type: "UserAlreadyAssignedException" as const,
		error_msg: "This user has const"
	},
	9: {
		status: 400 as const,
		error_type: "UsernameInUseException" as const,
		error_msg: "This username has const"
	},
	10: {
		status: 400 as const,
		error_type: "JobDosentExistException" as const,
		error_msg: `Job dosen't exist`
	},
	11: {
		status: 404 as const,
		error_type: "EventNotFoundException" as const,
		error_msg: `The Event dosen't exist`
	},
	12: {
		status: 404 as const,
		error_type: "DomainIsTooNewException" as const,
		error_msg: `The domain is too new`
	},
	13: {
		status: 400 as const,
		error_type: "TestAlreadyBeenComplete" as const,
		error_msg: `The test has already been completed`
	},
	400: {
		status: 400 as const,
		error_type: "InvalidRequestException" as const,
		error_msg: "Invalid Request"
	},
	401: {
		status: 401 as const,
		error_type: "UnauthorizedException" as const,
		error_msg: "Unauthorized Request"
	},
	404: {
		status: 404 as const,
		error_type: "NotFoundEndpointException" as const,
		error_msg: "Not Found Endpoint"
	},
	429: {
		status: 429 as const,
		error_type: "OAuthRateLimitException" as const,
		error_msg: "You made too many requests in 15 min"
	},
	500: {
		status: 500 as const,
		error_type: "UnHandleException" as const,
		error_msg: "UnHandleException"
	}
};

export function isRunningAsPWA(): boolean {
	return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
}

export function throwError<T extends keyof typeof errors>(number: T, error: any = null): (typeof errors)[T] & { number: T } {
	if (error != null) {
		errors[number]["error_msg"] = error;
	}
	return { ...errors[number], number: number };
}

export function AddScriptToHeader(
	src: string,
	props?: {
		id?: string;
		async?: boolean;
		crossorigin?: string;
	}
) {
	// @ts-ignore
	window.alreadyLoaded ||= [];
	// @ts-ignore
	if (window.alreadyLoaded.indexOf(src) > -1) {
		return Promise.resolve();
	} else {
		// @ts-ignore
		window.alreadyLoaded.push(src);
		return new Promise((resolve, reject) => {
			const script = document.createElement("script");
			if (props?.id) script.id = props.id;
			script.setAttribute("src", src);
			script.async = props?.async ?? true;
			// script.crossOrigin = props?.crossorigin ?? "anonymous";
			script.addEventListener("load", () => {
				resolve(1);
			});
			script.addEventListener("error", reject);
			document.head.appendChild(script);
		});
	}
}
/**
 * Busca un elemento en el DOM atravesando las barreras del Shadow DOM usando ">>>>"
 * @param {string} selector - El selector que contiene ">>>>" (ej. 'c2-jobs-published >>>> tr ct-icon >>>> span')
 * @param {Document|Element} root - El elemento inicial desde donde buscar (por defecto document)
 * @returns {Element|null} El elemento encontrado o null
 */
export function shadowSelector(selector: string, root: Document | Element = document) {
	// 1. Hacemos el split por el operador '>>>>' y limpiamos los espacios
	const parts = selector.split(">>>>").map(part => part.trim());

	let currentElement: Document | Element | ShadowRoot | null | undefined = root;

	// 2. Iteramos por cada fragmento del selector
	for (const part of parts) {
		if (!currentElement) return null;

		// Si no es el primer fragmento, significa que acabamos de cruzar un '>>>>'
		// por lo tanto, debemos entrar al shadowRoot del elemento actual antes de buscar
		if (currentElement !== root) {
			if (!("shadowRoot" in currentElement)) {
				console.warn("Se intentó cruzar un Shadow DOM, pero el elemento no tiene shadowRoot:", currentElement);
				return null;
			}
			currentElement = currentElement.shadowRoot;
		}

		// Buscamos la parte del selector dentro del contexto actual (DOM o Shadow DOM)
		currentElement = currentElement?.querySelector(part);
	}

	return currentElement;
}
// @ts-ignore For Unit testing
window.shadowSelector = shadowSelector;

export const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
	let timeout: ReturnType<typeof setTimeout>;

	return (...args: Parameters<T>) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			func(...args);
		}, delay);
	};
};

export function randomOrder<T = any>(array: T[]) {
	return array.sort(() => 0.5 - Math.random());
}

export function format(str: string, arr: any[]) {
	for (let i in arr) {
		str = str.replace(new RegExp(`\\{${i}\\}`, "gi"), arr[i]);
	}
	return str;
}
export function typeOf(obj: any) {
	return {}.toString.call(obj).split(" ")[1].slice(0, -1).toLowerCase();
}

export function clean(obj: any) {
	for (var propName in obj) {
		if (obj[propName] === null || obj[propName] === undefined) {
			delete obj[propName];
		}
	}
	return obj;
}
export let naturalSort = (o1: string, o2: string) => {
	return o1.localeCompare(o2);
	let fancyUint8Array = (arr: string) => arr.split("").map(a => a.charCodeAt(0));
	let aa = fancyUint8Array(o1);
	let bb = fancyUint8Array(o2);
	for (let i = 0; i < aa.length && i < bb.length; i++)
		if (aa[i] == bb[i]) continue;
		else return aa[i] - bb[i];
	return aa[0] - bb[0];
};
export async function unregisterServiceWorker() {
	debugger;
	try {
		let keyList = await caches.keys();
		let toDelete = ["js-v1", "workbox"];
		for (const key of keyList) {
			if (toDelete.some(v => key.includes(v))) await caches.delete(key);
		}
		let registrations = await navigator.serviceWorker.getRegistrations();
		for (let r of registrations) {
			if (r.active?.scriptURL.includes("service-worker.js")) await r.unregister();
		}
	} catch {}
	window.app.reload();
}

export function showInstaallApp(installprompt: BeforeInstallPromptEvent) {
	installprompt.prompt();

	installprompt.userChoise.then(choiceResult => {
		console.log(choiceResult.outcome);

		if (choiceResult.outcome == "dismissed") {
			console.log("User cancelled home screen install");
		} else {
			console.log("User added to home screen");
		}

		// We no longer need the prompt.  Clear it up.
		window.installprompt = undefined;
	});
}
export async function hasSW() {
	const registrations = await navigator.serviceWorker?.getRegistrations();
	return registrations?.filter(r => r.active?.scriptURL.includes("service-worker.js")).length > 0;
}

export function raw(strings: TemplateStringsArray, ...values: unknown[]) {
	const l = strings.length - 1;
	let html = "";
	for (let i = 0; i < l; i++) {
		let v = values[i];
		if (v instanceof Array) {
			v = v.join("");
		}
		html += strings[i] + v;
	}
	html += strings[l];
	return html;
}

export let updateMetaDescrip = (txt: string) => {
	let description = document.querySelector('meta[name="description"]');
	if (description == null) {
		description = document.createElement("meta");
		description.setAttribute("name", "description");
		document.head.appendChild(description);
	}
	description.setAttribute("content", txt);
};
export let updateStatusCode = (txt: string) => {
	let statusCode = document.querySelector('meta[name="render:status_code"]');
	if (statusCode == null) {
		statusCode = document.createElement("meta");
		statusCode.setAttribute("name", "render:status_code");
		document.head.appendChild(statusCode);
	}
	statusCode.setAttribute("content", txt);
};
export let setLDJSON = (value: string) => {
	let ldjson = document.querySelector('script[type="application/ld+json"]');
	if (ldjson == null) {
		ldjson = document.createElement("script");
		ldjson.setAttribute("type", "application/ld+json");
		document.head.appendChild(ldjson);
	}
	ldjson.innerHTML = value;
};

function replaceUTF8(str: string) {
	let chars = str.split("");
	for (let i = 0; i < chars.length; i++) {
		const c = chars[i];
		if (c.charCodeAt(0) > 255) {
			console.log("Caracter invalido en", i, " - ", c);
			chars[i] = `\"`;
		}
	}
	return chars.join("");
}

// @ts-ignore
export async function openFullscreen(elem: HTMLElement | Element): Promise<void> {
	try {
		await window.electron?.forceFullScreen();
		await window.android?.forceFullScreen();
		if (elem.requestFullscreen) return elem.requestFullscreen();
		// @ts-ignore Firefox
		else if (elem.mozRequestFullScreen) return elem.mozRequestFullScreen();
		// @ts-ignore Chrome, Safari and Opera
		else if (elem.webkitRequestFullscreen) return elem.webkitRequestFullscreen();
		// @ts-ignore IE/Edge
		else if (elem.msRequestFullscreen) return elem.msRequestFullscreen();
	} catch (e) {
		console.error("Error controlado", "openFullscreen", e);
	}
}
export function closeFullscreen(document: Document | any) {
	if (document.exitFullscreen) {
		document.exitFullscreen();
	} else if (document.mozCancelFullScreen) {
		/* Firefox */
		document.mozCancelFullScreen();
	} else if (document.webkitExitFullscreen) {
		/* Chrome, Safari and Opera */
		document.webkitExitFullscreen();
	} else if (document.msExitFullscreen) {
		/* IE/Edge */
		document.msExitFullscreen();
	}
}
export function addPastListener(ele: Document | HTMLElement = document, callback: (data: { blob: Blob | File }) => any, reject?: (e: any) => void) {
	ele.onpaste = function (event) {
		var items = event.clipboardData?.items;
		for (let item of items || []) {
			console.log(JSON.stringify({ kind: item.kind, type: item.type }));
			if (item.kind === "file") {
				var blob = item.getAsFile();
				var reader = new FileReader();
				reader.onload = function (event) {
					// console.log(event.target?.result);
					if (blob) callback({ blob: blob! });
				}; // data url!
				reader.onerror = e => {
					let err: ProgressEvent<FileReader> = e;
					console.error(err.target?.error);
					reject?.(e);
				};
				blob && reader.readAsDataURL(blob);
			} else {
				item.getAsString(console.log);
			}
		}
	};
}

export function Capitalize(s: string, all = false) {
	if (!all) return s.charAt(0).toUpperCase() + s.slice(1);
	let words = s.split(" ");
	let res = "";
	words.forEach(word => {
		res += word.charAt(0).toUpperCase() + word.slice(1) + " ";
	});
	return res;
}
// ==== Resize Img =====

let downScaleCanvas = function (cv: HTMLCanvasElement, scale: number) {
	if (!(scale <= 1) || !(scale > 0)) throw "scale must be a positive number <1 ";
	//scale = normaliseScale(scale);
	var sqScale = scale * scale; // square scale =  area of a source pixel within target
	var sw = cv.width; // source image width
	var sh = cv.height; // source image height
	var tw = Math.floor(sw * scale); // target image width
	var th = Math.floor(sh * scale); // target image height
	var sx = 0,
		sy = 0,
		sIndex = 0; // source x,y, index within source array
	var tx = 0,
		ty = 0,
		yIndex = 0,
		tIndex = 0; // target x,y, x,y index within target array
	var tX = 0,
		tY = 0; // rounded tx, ty
	var w = 0,
		nw = 0,
		wx = 0,
		nwx = 0,
		wy = 0,
		nwy = 0; // weight / next weight x / y
	// weight is weight of current source point within target.
	// next weight is weight of current source point within next target's point.
	var crossX = false; // does scaled px cross its current px right border ?
	var crossY = false; // does scaled px cross its current px bottom border ?
	var sBuffer = cv.getContext("2d")!.getImageData(0, 0, sw, sh).data; // source buffer 8 bit rgba
	var tBuffer = new Float32Array(3 * tw * th); // target buffer Float32 rgb
	var sR = 0,
		sG = 0,
		sB = 0; // source's current point r,g,b

	for (sy = 0; sy < sh; sy++) {
		ty = sy * scale; // y src position within target
		tY = 0 | ty; // rounded : target pixel's y
		yIndex = 3 * tY * tw; // line index within target array
		crossY = tY !== (0 | (ty + scale));
		if (crossY) {
			// if pixel is crossing botton target pixel
			wy = tY + 1 - ty; // weight of point within target pixel
			nwy = ty + scale - tY - 1; // ... within y+1 target pixel
		}
		for (sx = 0; sx < sw; sx++, sIndex += 4) {
			tx = sx * scale; // x src position within target
			tX = 0 | tx; // rounded : target pixel's x
			tIndex = yIndex + tX * 3; // target pixel index within target array
			crossX = tX !== (0 | (tx + scale));
			if (crossX) {
				// if pixel is crossing target pixel's right
				wx = tX + 1 - tx; // weight of point within target pixel
				nwx = tx + scale - tX - 1; // ... within x+1 target pixel
			}
			sR = sBuffer[sIndex]; // retrieving r,g,b for curr src px.
			sG = sBuffer[sIndex + 1];
			sB = sBuffer[sIndex + 2];
			if (!crossX && !crossY) {
				// pixel does not cross
				// just add components weighted by squared scale.
				tBuffer[tIndex] += sR * sqScale;
				tBuffer[tIndex + 1] += sG * sqScale;
				tBuffer[tIndex + 2] += sB * sqScale;
			} else if (crossX && !crossY) {
				// cross on X only
				w = wx * scale;
				// add weighted component for current px
				tBuffer[tIndex] += sR * w;
				tBuffer[tIndex + 1] += sG * w;
				tBuffer[tIndex + 2] += sB * w;
				// add weighted component for next (tX+1) px
				nw = nwx * scale;
				tBuffer[tIndex + 3] += sR * nw;
				tBuffer[tIndex + 4] += sG * nw;
				tBuffer[tIndex + 5] += sB * nw;
			} else if (!crossX && crossY) {
				// cross on Y only
				w = wy * scale;
				// add weighted component for current px
				tBuffer[tIndex] += sR * w;
				tBuffer[tIndex + 1] += sG * w;
				tBuffer[tIndex + 2] += sB * w;
				// add weighted component for next (tY+1) px
				nw = nwy * scale;
				tBuffer[tIndex + 3 * tw] += sR * nw;
				tBuffer[tIndex + 3 * tw + 1] += sG * nw;
				tBuffer[tIndex + 3 * tw + 2] += sB * nw;
			} else {
				// crosses both x and y : four target points involved
				// add weighted component for current px
				w = wx * wy;
				tBuffer[tIndex] += sR * w;
				tBuffer[tIndex + 1] += sG * w;
				tBuffer[tIndex + 2] += sB * w;
				// for tX + 1; tY px
				nw = nwx * wy;
				tBuffer[tIndex + 3] += sR * nw;
				tBuffer[tIndex + 4] += sG * nw;
				tBuffer[tIndex + 5] += sB * nw;
				// for tX ; tY + 1 px
				nw = wx * nwy;
				tBuffer[tIndex + 3 * tw] += sR * nw;
				tBuffer[tIndex + 3 * tw + 1] += sG * nw;
				tBuffer[tIndex + 3 * tw + 2] += sB * nw;
				// for tX + 1 ; tY +1 px
				nw = nwx * nwy;
				tBuffer[tIndex + 3 * tw + 3] += sR * nw;
				tBuffer[tIndex + 3 * tw + 4] += sG * nw;
				tBuffer[tIndex + 3 * tw + 5] += sB * nw;
			}
		} // end for sx
	} // end for sy

	// create result canvas
	var resCV = document.createElement("canvas");
	resCV.width = tw;
	resCV.height = th;
	var resCtx = resCV.getContext("2d")!;
	var imgRes = resCtx.getImageData(0, 0, tw, th);
	var tByteBuffer = imgRes.data;
	// convert float32 array into a UInt8Clamped Array
	var pxIndex = 0; //
	for (sIndex = 0, tIndex = 0; pxIndex < tw * th; sIndex += 3, tIndex += 4, pxIndex++) {
		tByteBuffer[tIndex] = 0 | tBuffer[sIndex];
		tByteBuffer[tIndex + 1] = 0 | tBuffer[sIndex + 1];
		tByteBuffer[tIndex + 2] = 0 | tBuffer[sIndex + 2];
		tByteBuffer[tIndex + 3] = 255;
	}
	// writing result to canvas.
	resCtx.putImageData(imgRes, 0, 0);
	return resCV;
};

export let resizeImage = function (settings: { file: File | Blob; maxSize: number; allowSVG?: boolean; mimeType?: `${string}/${string}` }): Promise<Blob | File> {
	let file = settings.file;
	let maxSize = settings.maxSize;
	let reader = new FileReader();
	let image = new Image();
	let cv = document.createElement("canvas");
	let dataURItoBlob = (dataURI: string) => {
		let bytes = dataURI.split(",")[0].indexOf("base64") >= 0 ? atob(dataURI.split(",")[1]) : unescape(dataURI.split(",")[1]);
		let mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
		let max = bytes.length;
		let ia = new Uint8Array(max);
		for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
		return new Blob([ia], { type: mime });
	};
	let downScaleImage = (img: HTMLImageElement, maxSize: number) => {
		let scale = 1;
		let imgCV = document.createElement("canvas");
		imgCV.width = img.width;
		imgCV.height = img.height;
		if (imgCV.width >= imgCV.height) {
			if (imgCV.width > maxSize) {
				scale = maxSize / imgCV.width;
			}
		} else if (imgCV.height > imgCV.width) {
			if (imgCV.height > maxSize) {
				scale = maxSize / imgCV.height;
			}
		}
		let imgCtx = imgCV.getContext("2d")!;
		imgCtx.fillStyle = "white";
		imgCtx.fillRect(0, 0, img.width, img.width);
		imgCtx.drawImage(img, 0, 0);
		return downScaleCanvas(imgCV, scale);
	};

	let resize = () => {
		let scaledImage = downScaleImage(image, maxSize);
		return dataURItoBlob(scaledImage.toDataURL(settings.mimeType || "image/jpeg", 0.8));
	};

	return new Promise((resolve, reject) => {
		if (!file.type.match(/image.*/)) {
			reject(new Error("Not an image"));
			return;
		}
		if (settings.allowSVG && file.type.startsWith("image/svg+xml")) {
			resolve(file);
			return;
		}
		reader.onload = readerEvent => {
			image.onload = () => {
				return resolve(resize());
			};
			image.src = readerEvent.target!.result as string;
		};
		reader.onerror = e => {
			let err: ProgressEvent<FileReader> = e;
			err.target?.error;
			console.error(err.target?.error);
			reject(e);
		};
		reader.readAsDataURL(file);
	});
};

export function resizeSquareImage(settings: { file: File | Blob; maxSize: number; mimeType?: `${string}/${string}` }): Promise<Blob> {
	var file = settings.file;
	var maxSize = settings.maxSize;
	var image = new Image();
	var cv = document.createElement("canvas");
	var dataURItoBlob = function (dataURI: string) {
		var bytes = dataURI.split(",")[0].indexOf("base64") >= 0 ? atob(dataURI.split(",")[1]) : unescape(dataURI.split(",")[1]);
		var mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
		var max = bytes.length;
		var ia = new Uint8Array(max);
		for (var i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
		return new Blob([ia], { type: mime });
	};
	var downScaleImage = function (img: HTMLImageElement, maxSize: number) {
		var scale = 1;
		var width = img.width;
		var height = img.height;
		var paddingx = 0;
		var paddingy = 0;
		var imgCV = document.createElement("canvas");
		imgCV.width = img.width;
		imgCV.height = img.height;
		if (imgCV.width >= imgCV.height) {
			if (imgCV.width > maxSize) {
				scale = maxSize / imgCV.width;
			}
			maxSize = width;
			paddingy = (maxSize - height) / 2;
		} else if (imgCV.height > imgCV.width) {
			if (imgCV.height > maxSize) {
				scale = maxSize / imgCV.height;
			}
			maxSize = height;
			paddingx = (maxSize - width) / 2;
		}
		imgCV.width = maxSize;
		imgCV.height = maxSize;
		var imgCtx = imgCV.getContext("2d")!;
		imgCtx.fillStyle = "white";
		imgCtx.fillRect(0, 0, maxSize, maxSize);
		imgCtx.drawImage(img, paddingx, paddingy);
		return downScaleCanvas(imgCV, scale);
	};

	var resize = function () {
		var scaledImage = downScaleImage(image, maxSize);
		return dataURItoBlob(scaledImage.toDataURL(settings.mimeType || "image/jpeg", 0.8));
	};

	return new Promise((ok, reject) => {
		if (!file.type.match(/image.*/)) {
			reject(new Error("Not an image"));
			return;
		}

		let reader = new FileReader();
		reader.onload = function (readerEvent) {
			image.onload = function () {
				return ok(resize());
			};
			image.src = readerEvent.target!.result as string;
		};
		reader.onerror = e => {
			let err: ProgressEvent<FileReader> = e;
			console.error(err.target?.error);
			reject(e);
		};
		reader.readAsDataURL(file);
	});
}

/**
 * Convierte valores RGB a representación hex (#rrggbb).
 */

/**
 * Retorna el color dominante en formato hex de un Blob que contiene una imagen.
 * @param blob Blob de la imagen
 */
export async function getDominantColorFromBlob(params: { blob?: Blob; url?: string; includeBW?: boolean } = {}): Promise<{
	hex: string;
	rgb: { r: number; g: number; b: number };
}> {
	function rgbToHex(r: number, g: number, b: number): string {
		return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
	}

	const img = new Image();
	if (params.url) {
		img.src = params.url;
	} else if (params.blob) {
		//check if the blob is an image
		if (!params.blob.type.startsWith("image")) {
			return { hex: "#000000", rgb: { r: 0, g: 0, b: 0 } };
		}
		// Crear una URL para el blob y asignarla a la imagen
		img.src = URL.createObjectURL(params.blob);
	} else {
		return { hex: "#000000", rgb: { r: 0, g: 0, b: 0 } };
	}

	// Esperar a que cargue la imagen
	await new Promise<void>(resolve => {
		img.onload = () => resolve();
	});

	const blockSize = 5; // Solo visitar cada 5 píxeles para acelerar el proceso
	const canvas = document.createElement("canvas");
	const context = canvas.getContext && canvas.getContext("2d");

	// Si no hay contexto 2D disponible
	if (!context) {
		return { hex: "#000000", rgb: { r: 0, g: 0, b: 0 } };
	}

	// Ajustar el tamaño del canvas a las dimensiones de la imagen
	const height = (canvas.height = img.naturalHeight || img.offsetHeight || img.height);
	const width = (canvas.width = img.naturalWidth || img.offsetWidth || img.width);

	// Dibujar la imagen en el canvas
	context.drawImage(img, 0, 0);

	let data: ImageData;

	// Intentar obtener los datos de la imagen (puede fallar por CORS)
	try {
		data = context.getImageData(0, 0, width, height);
	} catch (e) {
		// Error de seguridad (CORS), devolver color por defecto
		return { hex: "#000000", rgb: { r: 0, g: 0, b: 0 } };
	}

	const length = data.data.length;
	// data.data: [r, g, b, a, r, g, b, a, ...]

	let i = -4;
	let count = 0;
	const rgb = { r: 0, g: 0, b: 0 };

	// Recorrer los píxeles en saltos de `blockSize`
	while ((i += blockSize * 4) < length) {
		const r = data.data[i];
		const g = data.data[i + 1];
		const b = data.data[i + 2];

		// Omitir píxeles blancos exactos
		if (!params.includeBW) {
			if (r > 185 && g > 185 && b > 185) {
				continue;
			}
			// Omitir píxeles casi negros
			if (r < 75 && g < 75 && b < 75) {
				continue;
			}
		}

		count++;
		rgb.r += r;
		rgb.g += g;
		rgb.b += b;
	}

	if (count === 0) {
		// Si no se encontró ningún píxel que no fuera blanco, se devuelve #000000
		return { hex: "#000000", rgb: { r: 0, g: 0, b: 0 } };
	}

	// Calcular el promedio (usamos ~~ para truncar a entero)
	rgb.r = ~~(rgb.r / count);
	rgb.g = ~~(rgb.g / count);
	rgb.b = ~~(rgb.b / count);

	return {
		hex: rgbToHex(rgb.r, rgb.g, rgb.b),
		rgb
	};
}

export function clearNullish<T>(obj: T): T {
	for (const key in obj) {
		if (obj[key] === undefined) {
			delete obj[key];
		}
		if (obj[key] === "") {
			// @ts-ignore
			obj[key] = null;
		}
	}
	return obj;
}

export function splitFileInChunks(file: File) {
	var chunkSize = 1024 * 1024;
	var fileSize = file.size;
	var chunks = Math.ceil(file.size / chunkSize);
	var chunk = 0;

	console.log("file size..", fileSize);
	console.log("chunks...", chunks);
	let chunksData: Blob[] = [];

	while (chunk <= chunks) {
		var offset = chunk * chunkSize;
		console.log("current chunk..f", chunk);
		console.log("offset...", chunk * chunkSize);
		console.log("file blob from offset...", offset);
		let d = file.slice(offset, offset + chunkSize);
		chunksData.push(d);
		chunk++;
		console.log(d);
	}
	return chunksData;
}
export function sayHi({ sep = " ", displayName }: { sep?: string; displayName?: string } = {}) {
	if (sep == null) sep = " ";
	let cu: { displayName: string | null } = { displayName: "" };
	if (getAuth().currentUser && !getAuth().currentUser!.isAnonymous) {
		cu = getAuth().currentUser!;
	}
	let hours = new Date().getHours();
	let tt = "";
	if (hours < 12) {
		tt = strings.good_morning;
	} else if (hours < 18) {
		tt = strings.good_afternoon;
	} else {
		tt = strings.good_evening;
	}
	displayName = displayName || cu.displayName || "";
	tt += (displayName ? ", " : "") + sep + displayName;
	tt = tt.trim();
	return tt;
}

export let intToString = (val: number) => {
	if (val > 1000000) return Math.floor(Math.round(val) / 1000000).toFixed(1) + "M";
	else if (val > 1000) return Math.floor(Math.round(val) / 1000).toFixed(1) + "K";
	else return val + "";
};

export function timestampToString(timestamp: number | bigint, finish?: number | bigint, dim?: "min" | "hrs") {
	if (timestamp == finish) return "--";
	if (typeof timestamp === "bigint") timestamp = Number(timestamp);
	if (typeof finish === "bigint") finish = Number(finish);

	if (timestamp > 9999999999) timestamp = Math.floor(timestamp / 1000);
	let unixMili = (finish == null ? Math.round(new Date().getTime()) : finish * 1000) - timestamp * 1000; // Lo paso a milisegundos
	unixMili = Math.abs(unixMili);
	let unixSec = unixMili / 1000; // Lo regreso a segundos
	let unidad = strings.feed_seg;
	if (unixSec > 60) {
		// > 60 Seg -> min
		unixSec = unixMili / 60_000;
		unidad = strings.feed_minutos;
		if (unixSec > 60 && dim != "min") {
			// > 60 min -> hrs
			unixSec = unixMili / 3600_000;
			unidad = strings.hours;
			if (unixSec > 24 && dim != "hrs") {
				// > 24 hrs -> Dias
				unixSec = unixMili / 86_400_000;
				unidad = Math.floor(unixSec) > 1 ? strings.days : strings.day;
				if (unixSec > 7) {
					// > 7 dias -> Semanas
					unixSec = Math.floor(unixMili / 604_800_000);
					unidad = strings.weeks;
					if (unixSec > 4) {
						// > 4 semanas -> 1 Mes
						unixSec = unixMili / 2_592_000_000;
						unidad = Math.floor(unixSec) > 1 ? strings.months : strings.month;
						if (unixSec > 12) {
							// > 12 Meses -> 1 Año
							unixSec = unixMili / 31_536_000_000;
							unidad = Math.floor(unixSec) > 1 ? strings.years : strings.year;
						}
					}
				}
			}
		}
	}
	return Math.floor(unixSec) + " " + unidad;
}

export async function getServerTime() {
	interface HC {
		ip: `${number}.${number}.${number}.${number}`;
		country: string;
		country_name: string;
		region: string;
		region_code: string;
		city: string;
		postal: `${number}`;
		loc: `${number},${number}`;
	}

	let js: HC = await (await fetch(`https://geoip.healthcare.com/`)).json();
	// await fetch("https://maps.googleapis.com/maps/api/mapsjs/gen_204?csp_test=true")
	return js;
}

export function removeAcentoOld(input: string) {
	// Cadena de caracteres original a sustituir.
	let original = "áàäêéèëíìïóòöúùuñÁÀÄÉÈËÍÌÏÓÒÖÚÙÜÑçÇ";
	// Cadena de caracteres ASCII que reemplazarán los originales.
	let ascii = "aaaeeeeiiiooouuunAAAEEEIIIOOOUUUNcC";
	let output = input;
	for (let i = 0; i < ascii.length; i++) {
		// Reemplazamos los caracteres especiales.
		output = output.replace(original.charAt(i), ascii.charAt(i));
	}
	return output;
}

export function removeAcento(texto: string): string {
	return texto
		.normalize("NFD") // Normaliza la cadena a su forma de descomposición (ej: 'é' -> 'e' + '´')
		.replace(/[\u0300-\u036f]/g, ""); // Elimina los caracteres diacríticos en el rango Unicode
}
// Salida: El veloz murcielago hindu comia feliz cardillo y kiwi. La ciguena tocaba el saxofon detras del palenque de paja. ¡Pinguino, que canon!
export let removegtlt = (t: string) => {
	return t.replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

export function destroyEmailURL(text: string) {
	var urlRegex = /\s((https?:\/\/)?(www\.)?[-a-zA-Z0-9:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9:%_\+.~#?&=\/]*))/gi;
	text = text.replace(urlRegex, function (url) {
		return "****.***";
	});

	var regxMail = /([\w\.-]+)@([\w\.-]+)(\.[\w\.]+)/gi;
	text = text.replace(regxMail, function (email) {
		return "*****@***.***";
	});

	return text;
}

export function hasEmailOrURL(text: string) {
	var urlRegex = /\s((https?:\/\/)?(www\.)?[-a-zA-Z0-9:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9:%_\+.~#?&=\/]*))/gi;
	let has = false;
	let txt: string[] = [];
	text = text.replace(urlRegex, (url: string) => {
		txt.push(url);
		has = true;
		return "****.***";
	});

	var regxMail = /[_a-z0-9-]+(\.[_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})/gi;
	text = text.replace(regxMail, (email: string) => {
		txt.push(email);
		has = true;
		return "*****@***.***";
	});

	return { invalid: has, txt: txt };
}

export function ctEncode(str: string) {
	return encodeURIComponent(
		removeAcento(new String(str).toString())
			.replace(/[\s\-\/]/g, "_")
			.replace(/[\(\)]/g, "")
			.replace(/[%,]/g, "")
	);
}

export function toWhatsappLink(phone: string) {
	phone = phone.replace(/\+\s/gi, "");
	return `https://api.whatsapp.com/send?phone=${phone}`;
}

export function DecodeBase64URL(base64: string) {
	// Reemplazar caracteres de Base64URL por los de Base64 estándar
	base64 = base64.replace(/-/g, "+").replace(/_/g, "/");

	// Añadir relleno '=' si es necesario
	const pad = base64.length % 4;
	if (pad) {
		base64 += "=".repeat(4 - pad);
	}

	// Decodificar de Base64 a cadena binaria
	const binaryData = atob(base64);

	// Convertir cadena binaria a Uint8Array
	const bytes = Uint8Array.from(binaryData, char => char.charCodeAt(0));

	// Decodificar bytes a cadena UTF-8
	const decodedData = new TextDecoder("utf-8").decode(bytes);

	return decodedData;
}
export function EncodeBase64(str: string) {
	// Convertir la cadena Unicode a un Uint8Array de bytes UTF-8
	const utf8Bytes = new TextEncoder().encode(str);

	// Convertir los bytes a una cadena binaria
	const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join("");

	// Usar btoa para codificar la cadena binaria a Base64
	return btoa(binaryString);
}

function checkFirebaseConnection(): Promise<boolean> {
	return new Promise(resolve => {
		const img = new Image();
		img.src = `https://www.google.com/favicon.ico?rand=${Math.random()}`; // Evitar caché
		img.onload = () => resolve(true);
		img.onerror = () => resolve(false);
	});
}

export function urlify(text: string) {
	text = " " + text;

	// // @HerberthObregon
	// var regxMencion = /([\s\n\b])@(?![_.])(?!.*[_.]{2})([a-z0-9._]+[a-z0-9_])/gi;
	// text = text.replace(regxMencion, function (m) {
	// 	m = m.replace(/\s/gi, "").replace(/\n/gi, "");
	// 	return ' <a target="_blank" href="/' + m.replace("@", "") + '">' + m + "</a>";
	// });

	// URLS
	var urlRegex = /[\s\b\n](https?:\/\/)?(www\.)?[-a-z0-9:%._\+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9:%_\+.~#?&\/\/=]*)/gi;
	text = text.replace(urlRegex, function (url) {
		url = url
			.replace(/\s/gi, "")
			.replace(/\n/gi, "")
			.replace(/https?:\/\//gi, "");
		return ` <a target="_blank" href="https://${url}">https://${url}</a>`;
	});

	// info@empleo.gt
	var regxMail = /[_a-z0-9-]+(\.[_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})/gi;
	text = text.replace(regxMail, function (email) {
		return '<a target="_blank" href="mailto:' + email + '">' + email + "</a>";
	});

	// #Guatemala
	var regx = /(#[a-z0-9][a-z0-9áàäêéèëíìïóòöúùuñÁÀÄÉÈËÍÌÏÓÒÖÚÙÜÑçÇ\-_]*)/gi;
	text = text.replace(regx, function (hastag) {
		let hastagURL = hastag.replace("#", "");
		return '<a target="_blank" href="https://twitter.com/hashtag/' + hastagURL + '">' + hastag + "</a>";
	});

	return text.trim();
	// or alternatively
	// return text.replace(urlRegex, '<a href="$1">$1</a>')
}

/**
 * Obtiene los username de algun texto (un Post) y retorna un array
 * @param text Texto a evaluar
 * @returns {Array} Array de nombres de usuario sin @
 */
export function getUsernamesFromText(text: string, prefix = "@"): Array<string> {
	if (text[0] == prefix) text = text.replace(prefix, ` ${prefix}`);

	let list = [];
	// @HerberthObregon
	var regxMencion = /([\s\n\b])@(?![_.])(?!.*[_.]{2})([a-z0-9._]+[a-z0-9_])/gi;
	if (prefix == "#") regxMencion = /([\s\n\b])#(?![_.])(?!.*[_.]{2})([a-z0-9._]+[a-z0-9_])/gi;
	let re;
	while ((re = regxMencion.exec(text)) != null) {
		list.push(re[2]);
	}
	return list;
}
export function getStorageURL(url?: string | null, placeholder: string | undefined = window.app.constants.placeholder) {
	try {
		if (url == null || new String(url).length == 0 || (url && url.indexOf("facebook") > -1)) return placeholder;
		if ((url && (url.startsWith("http") || url.startsWith("www"))) || url.startsWith("/")) return url;
		else return window.app.constants.baseStorageURL() + url;
	} catch (e) {
		console.error(e);
		return placeholder;
	}
}

export async function copyTextToClipboard(text: string) {
	try {
		if (window.navigator?.clipboard?.writeText != undefined) {
			console.log("navigator.clipboard");
			try {
				await navigator.clipboard.writeText(text);
				return true;
			} catch (e) {
				console.log("navigator.clipboard Failed", e);
			}
		}
		var textArea = document.createElement("textarea");
		textArea.style.position = "fixed";
		textArea.style.top = "0";
		textArea.style.left = "0";
		textArea.style.width = "2em";
		textArea.style.height = "2em";
		textArea.style.padding = "0";
		textArea.style.border = "none";
		textArea.style.outline = "none";
		textArea.style.boxShadow = "none";
		textArea.style.background = "transparent";
		textArea.value = text;
		document.body.appendChild(textArea);
		textArea.select();

		var successful = document.execCommand("copy");
		var msg = successful ? "successful" : "unsuccessful";
		console.log(text, " - Copying text command was " + msg);
		document.body.removeChild(textArea);
		return successful;
	} catch (e) {
		console.log("Oops, unable to copy");
	}
}
